import { type FormEvent, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import { api } from "@/api/client";
import { useAuth } from "@/auth/AuthProvider";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { mapApiError } from "@/lib/errors";
import { Loader2 } from "lucide-react";

type Step = "confirm" | "approved" | "error";

export function DeviceApprovePage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const codeFromUrl = searchParams.get("code") ?? "";

  const [userCode, setUserCode] = useState(codeFromUrl);
  const [step, setStep] = useState<Step>("confirm");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (codeFromUrl) setUserCode(codeFromUrl);
  }, [codeFromUrl]);

  async function handleApprove(e: FormEvent) {
    e.preventDefault();
    if (!userCode.trim()) return;
    setError("");
    setLoading(true);
    try {
      await api("/api/v2/auth/device/approve", {
        method: "POST",
        body: JSON.stringify({ user_code: userCode.trim() }),
      });
      setStep("approved");
    } catch (err) {
      setError(
        mapApiError(err, { 404: t("deviceApprove.codeNotFound") }, t("deviceApprove.genericError")),
      );
      setStep("error");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <div className="w-full max-w-sm space-y-6">
        <div className="text-center">
          <h1 className="text-2xl font-bold">RaiSE</h1>
        </div>
        <Card>
          <CardHeader className="text-center">
            <CardTitle className="text-xl">{t("deviceApprove.title")}</CardTitle>
            <CardDescription>
              {step === "approved"
                ? t("deviceApprove.approvedSubtitle")
                : t("deviceApprove.confirmSubtitle")}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {step === "approved" ? (
              <div className="space-y-4 text-center">
                <div className="rounded-md bg-muted p-4">
                  <p className="text-lg font-semibold text-green-600">{t("deviceApprove.approvedLabel")}</p>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {t("deviceApprove.approvedMessage")}
                  </p>
                </div>
                <p className="text-xs text-muted-foreground">
                  <Trans i18nKey="deviceApprove.signedInAs" values={{ email: user?.email }}>
                    Signed in as {user?.email}
                  </Trans>
                </p>
              </div>
            ) : (
              <form onSubmit={handleApprove} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="user-code">{t("deviceApprove.codeLabel")}</Label>
                  <Input
                    id="user-code"
                    type="text"
                    placeholder="ABCD-1234"
                    value={userCode}
                    onChange={(e) => setUserCode(e.target.value)}
                    required
                    disabled={loading}
                    className="text-center text-lg font-mono tracking-widest"
                    autoFocus={!codeFromUrl}
                  />
                </div>
                {error && <p className="text-sm text-destructive">{error}</p>}
                <Button
                  type="submit"
                  className="w-full"
                  disabled={loading || !userCode.trim()}
                >
                  {loading ? (
                    <>
                      <Loader2 className="animate-spin" />
                      {t("deviceApprove.approving")}
                    </>
                  ) : (
                    t("deviceApprove.approveCta")
                  )}
                </Button>
                <p className="text-xs text-center text-muted-foreground">
                  <Trans i18nKey="deviceApprove.approvingAs" values={{ email: user?.email }}>
                    Approving as {user?.email}
                  </Trans>
                </p>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
